import { faCalendarAlt } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { forwardRef } from 'react';
import ReactDatePicker, { CalendarContainer } from 'react-datepicker';
import styled, { ThemeColors, createGlobalStyle } from 'styled-components';
import { useTranslation } from 'react-i18next';
import Input from './Input';
// @ts-ignore - allow importing datepicker CSS in TSX
import 'react-datepicker/dist/react-datepicker.css';

type Filter = 'None' | 'Weekdays' | 'Future' | 'History';

type DatepickerProps = {
  selectedDate?: Date | null;
  startDate?: Date;
  endDate?: Date;
  onChange: (
    date: Date | [Date, Date] | null,
    event?: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>,
  ) => void;
  header?: string;
  customInput?: React.ReactNode;
  className?: string;
  bgColor?: keyof ThemeColors;
  showIcon?: boolean;
  filter?: Filter;
  autofocus?: boolean;
  minDate?: Date;
  maxDate?: Date;
  name?: string;
  disabled?: boolean;
};

const Datepicker = ({
  startDate,
  endDate,
  selectedDate,
  onChange,
  header,
  customInput,
  className,
  bgColor,
  showIcon,
  filter = 'Weekdays',
  autofocus,
  minDate,
  maxDate,
  name,
  disabled,
}: DatepickerProps) => {
  const { i18n } = useTranslation();

  const DatepickerActions: React.FC<{ className?: string; children?: React.ReactNode }> = ({ className, children }) => {
    return (
      <>
        <Header className="f1-400 mb-1">{header}</Header>
        <div className={className}>{children}</div>
      </>
    );
  };

  const DatePickerInput = forwardRef(
    (props: React.InputHTMLAttributes<HTMLInputElement>, ref: React.Ref<HTMLInputElement>) => (
      <DateWrapper>
        <DateInput
          name={name}
          ref={ref}
          autoFocus={autofocus}
          onClick={props.onClick}
          bgColor={bgColor}
          className={`${props.className} ${showIcon ? 'text-left' : ''}`}
          value={typeof props.value === 'string' ? props.value : ''}
          onChange={props.onChange}
          disabled={disabled}
        />
        {showIcon && (
          <IconWrapper onClick={props.onClick}>
            <Icon icon={faCalendarAlt} />
          </IconWrapper>
        )}
      </DateWrapper>
    ),
  );

  return (
    <>
      <DatepickerOverrides />

      <ReactDatePicker
      wrapperClassName="w-100"
      className={className}
      calendarContainer={header ? DatepickerActions : CalendarContainer}
      filterDate={getFilter(filter)}
      selected={selectedDate}
      startDate={startDate}
      endDate={endDate}
      dateFormat="P"
      locale={i18n.language}
      minDate={minDate}
      maxDate={maxDate}
      customInput={customInput ? <CustomInput>{customInput}</CustomInput> : <DatePickerInput />}
      onChange={onChange}
      showPopperArrow={false}
      popperPlacement="bottom-end"
    />
    </>
  );
};

function getFilter(type?: Filter) {
  if (!type || type === 'None') return;

  if (type === 'Weekdays') return filterWeekdays;
  if (type === 'History') return filterHistory;
  if (type === 'Future') return filterFuture;
}

const filterWeekdays = (date: Date) => {
  const day = date.getDay();
  return day !== 0 && day !== 6;
};

const filterFuture = (date: Date) => {
  const currentDate = new Date();
  return currentDate < date;
};

const filterHistory = (date: Date) => {
  const currentDate = new Date();
  return currentDate > date;
};

export default Datepicker;

const DatepickerOverrides = createGlobalStyle`
  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    color: ${({ theme }) => theme.colors.fillWhite} !important;
  }

  .react-datepicker__day--selected:hover,
  .react-datepicker__day--keyboard-selected:hover {
    color: ${({ theme }) => theme.colors.fillWhite} !important;
  }
`;

const DateWrapper = styled.div`
  left: 200px;
`;

const IconWrapper = styled.div`
  position: absolute;
  right: 15px;
  width: 14px;
  display: flex;
  top: 0;
  height: calc(100% - 2px);
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Icon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 16px;
`;

const DateInput = styled(Input)<{ bgColor?: keyof ThemeColors }>`
  width: 100%;
`;

const Header = styled.div`
  background: ${({ theme }) => theme.colors.fillWhite};
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  font-size: 13px;
  padding: 1rem;
  ${({ theme }) => theme.shadows.low};
`;

const CustomInput = styled.div``;
